/**
 * API Client Barrel Export
 * 
 * Provides a unified interface for all API operations.
 * Import from this file for type-safe API access.
 * 
 * @example
 * import { api, type Pool, type ApiResponse } from '@/services/api';
 * 
 * const response = await api.pools.getPool('pool-123');
 * if (response.data) {
 *   console.log(response.data.name);
 * }
 */

// Re-export all types
export type {
  // Entity types
  Pool,
  PoolWithMembership,
  Membership,
  MemberWithSubmissionStatus,
  Pick,
  DeathRecord,
  Celebrity,
  LeaderboardEntry,
  PlayerPicks,
  PoolStatus,
  MembershipRole,
  AuditAction,
  // API types
  ApiResponse,
  ApiError,
  ApiMeta,
  // Input types
  CreatePoolInput,
  UpdatePoolInput,
  SubmitPicksInput,
  PickInput,
  DeathInput,
  JoinPoolInput,
  CreateCustomCelebrityInput,
  // Celebrity details types
  GlobalDeathRecord,
  CelebrityStats,
  CelebrityAuditEvent,
  CelebrityDetailsResponse,
  // Automated death detection types
  DeathSource,
  PendingDeathRecord,
  PendingDeathStatus,
  ScraperRunResult,
  CreateDeathSourceInput,
  UpdateDeathSourceInput,
  ApprovePendingDeathInput,
  RejectPendingDeathInput,
} from './types';

// Re-export admin types
export type {
  AddGlobalDeathInput,
  AddGlobalDeathResult,
  UpdateGlobalDeathInput,
  UpdateGlobalDeathResult,
  ListGlobalDeathsOptions,
  PaginatedGlobalDeaths,
  UpdateCustomCelebrityInput,
  MergeCelebritiesInput,
  MergeCelebritiesResult,
  CustomCelebrityWithStats,
  ListCustomCelebritiesOptions,
  PaginatedCustomCelebrities,
  ApprovePendingDeathResult,
} from './admin';

// Re-export mulligan types
export type {
  MulliganWindow,
  MulliganWindowWithOpenState,
  CreateMulliganWindowInput,
  UpdateMulliganWindowInput,
  MulliganSwapRequest,
  MulliganSwapResponse,
  MulliganStatusResponse,
  MulliganStatusWindowEntry,
  MulliganStatusLockedPick,
  LockedPickReason,
  MulliganAuditEvent,
  MulliganAuditPage,
  MulliganAuditQuery,
  ListMulliganWindowsResponse,
} from './mulligan.types';

// Import API functions
import * as poolsApi from './pools';
import * as membershipApi from './membership';
import * as picksApi from './picks';
import * as celebritiesApi from './celebrities';
import * as leaderboardApi from './leaderboard';
import * as deathsApi from './deaths';
import * as adminApi from './admin';
import * as mulliganApi from './mulligan';

// Re-export individual functions for direct imports
export {
  createPool,
  getPool,
  updatePool,
  getPoolMembers,
  removeMember,
  promoteToCohost,
  lockPool,
  unlockPool,
} from './pools';

export { joinPool, getMyPools } from './membership';

export { submitPicks, getMyPicks, getAllPicks } from './picks';

export { searchCelebrities, createCustomCelebrity, getCelebrity, getCelebrityDetails } from './celebrities';

export { getLeaderboard } from './leaderboard';

export { recordDeath, getDeaths } from './deaths';

// Re-export admin functions
export {
  addGlobalDeath,
  removeGlobalDeath,
  updateGlobalDeath,
  listGlobalDeaths,
  refreshCelebrityCache,
  invalidateCelebrityCache,
  updateCustomCelebrity,
  mergeCustomCelebrities,
  deleteCustomCelebrity,
  listCustomCelebrities,
  // Death source management
  listDeathSources,
  createDeathSource,
  updateDeathSource,
  deleteDeathSource,
  scrapeSource,
  runScraper,
  // Pending death management
  listPendingDeaths,
  approvePendingDeath,
  rejectPendingDeath,
} from './admin';

// Re-export mulligan functions
export {
  listMulliganWindows,
  createMulliganWindow,
  updateMulliganWindow,
  deleteMulliganWindow,
  getMulliganStatus,
  applyMulliganSwap,
  getMulliganAudit,
} from './mulligan';

// Re-export mulligan error class
export { MulliganApiError, isMulliganApiError } from './mulligan.errors';

// Export token management utilities
export { setTokens, clearTokens } from './client';

/**
 * Grouped API client for organized access to all endpoints
 * 
 * @example
 * import { api } from '@/services/api';
 * 
 * // Pool operations
 * const pool = await api.pools.createPool({ name: 'My Pool', ... });
 * 
 * // Membership operations
 * const membership = await api.membership.joinPool('ABC12345');
 * 
 * // Pick operations
 * const picks = await api.picks.submitPicks(poolId, { picks: [...] });
 */
export const api = {
  pools: {
    createPool: poolsApi.createPool,
    getPool: poolsApi.getPool,
    updatePool: poolsApi.updatePool,
    getPoolMembers: poolsApi.getPoolMembers,
    removeMember: poolsApi.removeMember,
    promoteToCohost: poolsApi.promoteToCohost,
    lockPool: poolsApi.lockPool,
    unlockPool: poolsApi.unlockPool,
  },
  membership: {
    joinPool: membershipApi.joinPool,
    getMyPools: membershipApi.getMyPools,
  },
  picks: {
    submitPicks: picksApi.submitPicks,
    getMyPicks: picksApi.getMyPicks,
    getAllPicks: picksApi.getAllPicks,
  },
  celebrities: {
    searchCelebrities: celebritiesApi.searchCelebrities,
    createCustomCelebrity: celebritiesApi.createCustomCelebrity,
    getCelebrity: celebritiesApi.getCelebrity,
    getCelebrityDetails: celebritiesApi.getCelebrityDetails,
  },
  leaderboard: {
    getLeaderboard: leaderboardApi.getLeaderboard,
  },
  deaths: {
    recordDeath: deathsApi.recordDeath,
    getDeaths: deathsApi.getDeaths,
  },
  admin: {
    addGlobalDeath: adminApi.addGlobalDeath,
    removeGlobalDeath: adminApi.removeGlobalDeath,
    updateGlobalDeath: adminApi.updateGlobalDeath,
    listGlobalDeaths: adminApi.listGlobalDeaths,
    refreshCelebrityCache: adminApi.refreshCelebrityCache,
    invalidateCelebrityCache: adminApi.invalidateCelebrityCache,
    updateCustomCelebrity: adminApi.updateCustomCelebrity,
    mergeCustomCelebrities: adminApi.mergeCustomCelebrities,
    deleteCustomCelebrity: adminApi.deleteCustomCelebrity,
    listCustomCelebrities: adminApi.listCustomCelebrities,
    // Death source management
    listDeathSources: adminApi.listDeathSources,
    createDeathSource: adminApi.createDeathSource,
    updateDeathSource: adminApi.updateDeathSource,
    deleteDeathSource: adminApi.deleteDeathSource,
    scrapeSource: adminApi.scrapeSource,
    runScraper: adminApi.runScraper,
    // Pending death management
    listPendingDeaths: adminApi.listPendingDeaths,
    approvePendingDeath: adminApi.approvePendingDeath,
    rejectPendingDeath: adminApi.rejectPendingDeath,
  },
  mulligan: {
    listMulliganWindows: mulliganApi.listMulliganWindows,
    createMulliganWindow: mulliganApi.createMulliganWindow,
    updateMulliganWindow: mulliganApi.updateMulliganWindow,
    deleteMulliganWindow: mulliganApi.deleteMulliganWindow,
    getMulliganStatus: mulliganApi.getMulliganStatus,
    applyMulliganSwap: mulliganApi.applyMulliganSwap,
    getMulliganAudit: mulliganApi.getMulliganAudit,
  },
} as const;

// Default export for convenience
export default api;
